<template>
  <tab-button
    :selected="evaluationId == currentEvaluationId"
    :closable="selectedEvaluation?.isDefaultSimulation == false"
    :renamable="selectedEvaluation?.isDefaultSimulation == false"
    :default-title="defaultTitle"
    :title="
      selectedEvaluation?.isDefaultSimulation
        ? $t('labels.openSimulation')
        : title
    "
    @close="close"
    @select="$emit('select')"
    @update:title="updateTitle($event)"
  >
    <template #icon>
      <lucide-icon
        v-if="chartConfig"
        :name="chartConfig.icon"
        style="margin-right: 3px; margin-bottom: -2px"
      />
    </template>

    <template #context-menu="{ startRenaming }">
      <button
        v-if="selectedEvaluation?.isDefaultSimulation == false"
        class="context-menu__entry"
        type="button"
        @click="startRenaming()"
      >
        {{ $t("actions.rename") }}
      </button>
      <button class="context-menu__entry" type="button" @click="duplicate()">
        {{ $t("actions.duplicate") }}
      </button>
      <button
        type="button"
        class="context-menu__entry"
        :disabled="!chartConfig"
        @click="$emit('download')"
      >
        {{ $t("actions.downloadChartAsImage") }}
      </button>
      <button
        v-if="selectedEvaluation?.isDefaultSimulation == false"
        type="button"
        class="context-menu__entry"
        @click="deleteEvaluation(selectedEvaluation.id)"
      >
        {{ $t("actions.deleteChart") }}
      </button>
    </template>
  </tab-button>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useEvaluationStore } from "@editor/items/evaluation/evaluations.store";
import { useEvaluationViewStore } from "@editor/views/evaluation/evaluationView.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import TabButton from "@core/ui/TabButton.vue";

import { charts } from "@editor/items/charts/charts";
import { useChartData } from "@editor/items/charts/useChartData";
import { useChartTitle } from "../charts/useChartTitle";

import type { EvaluationItem } from "@editor/items/evaluation/EvaluationItem";

const props = defineProps<{
  evaluationId: number;
}>();
const emit = defineEmits<{
  (e: "select"): void;
  (e: "close"): void;
  (e: "download"): void;
  (e: "create", evaluation: EvaluationItem): void;
}>();


const evaluationStore = useEvaluationStore();

const selectedEvaluation = computed(() => {
  return evaluationStore.items[props.evaluationId] as EvaluationItem | null;
});

const {
  chartAxisConfig,
  selectedItems,
  selectedSimulations,
  availableItems,
  dataToggles
} = useChartData(selectedEvaluation);
const { title, generatedTitleParts } = useChartTitle(() => selectedEvaluation.value?.title, () => ({
  singleSimulation: chartAxisConfig.value?.singleSimulation,
  dataToggles: dataToggles.value,
  selectedItems: selectedItems.value,
  selectedSimulations: selectedSimulations.value,
  availableItems: availableItems.value,
}));

// =============
// COMPUTED

const chartConfig = computed(() => {
  if (selectedEvaluation.value?.chartType == null) return null;
  return charts[selectedEvaluation.value.chartType];
});
const currentEvaluationId = computed(() => {
  const evaluation = useEvaluationViewStore().currentEvaluation;
  if (!evaluation) return null;
  return evaluation.id;
});
const defaultTitle = computed(() => {
  return generatedTitleParts.value.map((p) => p.text).join("");
});

// =================
// Methods

async function duplicate() {
  const newEvaluation = await evaluationStore.duplicate(
    props.evaluationId,
    {
      isDefaultSimulation: false
    }
  );

  emit("create", newEvaluation);
}

async function deleteEvaluation(evaluationId: number | null) {
  if (evaluationId == null) return;
  close();

  try {
    return await evaluationStore.delete({ id: evaluationId });
  } catch (error) {
    console.log((error as Error).message);
  }
}

function close() {
  // update title on close.
  evaluationStore.update({
    id: props.evaluationId,
    title: title.value
  });
  emit("close");
}

function updateTitle(newTitle: string) {
  const oldTitle = selectedEvaluation.value?.title ?? "";
  evaluationStore.update({
    id: props.evaluationId,
    title: newTitle || oldTitle,
    titleIsCustom: !!newTitle
  });
}
</script>
